import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Receipt,
  Trash2,
  Building2,
  User,
  Calendar,
  Download,
  FileText,
  CheckCircle2,
  Clock,
  RefreshCw,
  AlertCircle,
  History,
  Pencil,
  TrendingUp,
  X,
  Ban,
  RotateCcw
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { rentPaymentsAPI, propertiesAPI, clientsAPI, dueNoticesAPI, transactionsAPI } from '../lib/api';
import { formatCurrency, formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';
import ModuleTasksPanel from '../components/ModuleTasksPanel';
import { SyncBadge } from '../components/SyncBadge';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const paymentMethodLabels = {
  especes: 'Espèces',
  virement: 'Virement',
  mobile_money: 'Mobile Money',
  cheque: 'Chèque'
};

const statusLabels = {
  en_attente: { label: 'En attente', badgeClass: 'badge-status badge-late', icon: Clock },
  confirme: { label: 'Confirmé', badgeClass: 'badge-status badge-paid', icon: CheckCircle2 },
  annule: { label: 'Annulé', badgeClass: 'badge-status badge-unpaid', icon: X }
};

const termTypeLabels = {
  terme_echu: 'Terme échu (mois passé)',
  terme_a_echoir: 'Terme à échoir (mois en cours/futur)'
};

// Helper function to calculate period dates based on term type
const calculatePeriodDates = (termType, paymentDate) => {
  const date = paymentDate || new Date();
  let periodStart, periodEnd;
  
  if (termType === 'terme_echu') {
    // Terme échu: previous month
    const prevMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    periodStart = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1);
    periodEnd = new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 0); // Last day of prev month
  } else {
    // Terme à échoir: current month
    periodStart = new Date(date.getFullYear(), date.getMonth(), 1);
    periodEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0); // Last day of current month
  }
  
  return { period_start: periodStart, period_end: periodEnd };
};

const initialFormData = {
  property_id: '',
  tenant_id: '',
  amount: '',
  currency: 'XOF',
  payment_date: new Date(),
  term_type: 'terme_echu', // Default to terme échu
  period_start: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
  period_end: new Date(new Date().getFullYear(), new Date().getMonth(), 0),
  payment_method: 'especes',
  reference: '',
  notes: '',
};

const RentPayments = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('filter') || 'all');
  const [payments, setPayments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isHistoricalFormOpen, setIsHistoricalFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [historicalFormData, setHistoricalFormData] = useState({
    property_id: '',
    tenant_id: '',
    amount: '',
    currency: 'XOF',
    payment_date: new Date(),
    period_month: new Date().getMonth() + 1,
    period_year: new Date().getFullYear(),
    payment_method: 'especes',
    reference: '',
    notes: '',
    generate_receipt: true
  });
  const [saving, setSaving] = useState(false);
  const [generatingNotices, setGeneratingNotices] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(null);
  
  // Edit modal states
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    payment_date: new Date(),
    period_start: new Date(),
    period_end: new Date(),
    payment_method: 'especes',
    reference: '',
    notes: ''
  });
  const [editingPayment, setEditingPayment] = useState(null);

  // Prorata expected-amount info for current period (lease-aware)
  const [expectedRent, setExpectedRent] = useState(null);
  const [historicalExpectedRent, setHistoricalExpectedRent] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fetch the prorated/full expected rent when the lease + period_start change
  useEffect(() => {
    let cancelled = false;
    if (!isFormOpen) { setExpectedRent(null); return; }
    if (!formData.property_id || !formData.tenant_id || !formData.period_start) { setExpectedRent(null); return; }
    const lease = transactions.find(
      t => t.property_id === formData.property_id
        && t.client_id === formData.tenant_id
        && t.transaction_type === 'location'
    );
    if (!lease) { setExpectedRent(null); return; }
    const ps = formData.period_start instanceof Date ? formData.period_start : new Date(formData.period_start);
    rentPaymentsAPI.getExpectedAmount({
      transaction_id: lease.id,
      year: ps.getFullYear(),
      month: ps.getMonth() + 1,
    }).then(res => {
      if (cancelled) return;
      setExpectedRent(res.data);
      // Auto-update amount only when prorata kicks in to avoid surprising the user
      if (res.data?.is_prorated && res.data?.expected_amount > 0) {
        setFormData(prev => ({ ...prev, amount: String(res.data.expected_amount) }));
      }
    }).catch(() => { if (!cancelled) setExpectedRent(null); });
    return () => { cancelled = true; };
  }, [isFormOpen, formData.property_id, formData.tenant_id, formData.period_start, transactions]);

  // Same logic for the Historical payment modal (uses explicit period_month/year)
  useEffect(() => {
    let cancelled = false;
    if (!isHistoricalFormOpen) { setHistoricalExpectedRent(null); return; }
    const { property_id, tenant_id, period_month, period_year } = historicalFormData;
    if (!property_id || !tenant_id || !period_month || !period_year) { setHistoricalExpectedRent(null); return; }
    const lease = transactions.find(
      t => t.property_id === property_id
        && t.client_id === tenant_id
        && t.transaction_type === 'location'
    );
    if (!lease) { setHistoricalExpectedRent(null); return; }
    rentPaymentsAPI.getExpectedAmount({
      transaction_id: lease.id,
      year: period_year,
      month: period_month,
    }).then(res => {
      if (cancelled) return;
      setHistoricalExpectedRent(res.data);
      if (res.data?.is_prorated && res.data?.expected_amount > 0) {
        setHistoricalFormData(prev => ({ ...prev, amount: String(res.data.expected_amount) }));
      }
    }).catch(() => { if (!cancelled) setHistoricalExpectedRent(null); });
    return () => { cancelled = true; };
  }, [isHistoricalFormOpen, historicalFormData.property_id, historicalFormData.tenant_id, historicalFormData.period_month, historicalFormData.period_year, transactions]);

  // Filter tenants based on selected property
  const getTenantsForProperty = useCallback((propertyId) => {
    if (!propertyId) return [];
    
    // Get active transactions for this property
    const propertyTransactions = transactions.filter(t => t.property_id === propertyId);
    const tenantIds = propertyTransactions.map(t => t.client_id);
    
    // Return tenants that have an active lease on this property
    return tenants.filter(t => tenantIds.includes(t.id));
  }, [transactions, tenants]);

  // Filtered tenants for new payment form
  const filteredTenantsForForm = useMemo(() => {
    return getTenantsForProperty(formData.property_id);
  }, [formData.property_id, getTenantsForProperty]);

  // Filtered tenants for historical payment form
  const filteredTenantsForHistorical = useMemo(() => {
    return getTenantsForProperty(historicalFormData.property_id);
  }, [historicalFormData.property_id, getTenantsForProperty]);

  // Loyer mensuel exact du bail selectionne (locataire + bien)
  const findLeaseRent = useCallback((propertyId, tenantId) => {
    if (!propertyId || !tenantId) return null;
    const lease = transactions.find(
      t => t.property_id === propertyId
        && t.client_id === tenantId
        && t.transaction_type === 'location'
    );
    if (!lease) return null;
    return {
      amount: lease.monthly_rent || lease.amount,
      currency: lease.currency || 'XOF',
    };
  }, [transactions]);

  const newPaymentLeaseRent = useMemo(
    () => findLeaseRent(formData.property_id, formData.tenant_id),
    [formData.property_id, formData.tenant_id, findLeaseRent]
  );
  const historicalLeaseRent = useMemo(
    () => findLeaseRent(historicalFormData.property_id, historicalFormData.tenant_id),
    [historicalFormData.property_id, historicalFormData.tenant_id, findLeaseRent]
  );

  const fetchData = async () => {
    try {
      const [paymentsRes, propRes, clientRes, transRes] = await Promise.all([
        rentPaymentsAPI.getAll(),
        propertiesAPI.getAll(),
        clientsAPI.getAll(),
        transactionsAPI.getAll({ transaction_type: 'location' })
      ]);
      setPayments(paymentsRes.data);
      setProperties(propRes.data.filter(p => p.transaction_type === 'location'));
      setTenants(clientRes.data.filter(c => c.client_type === 'locataire'));
      setTransactions(transRes.data.filter(t => t.lease_status !== 'termine'));
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = () => {
    setFormData(initialFormData);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setFormData(initialFormData);
  };

  const handleOpenHistoricalForm = () => {
    setHistoricalFormData({
      property_id: '',
      tenant_id: '',
      amount: '',
      currency: 'XOF',
      payment_date: new Date(),
      period_month: new Date().getMonth(), // Previous month by default
      period_year: new Date().getFullYear(),
      payment_method: 'especes',
      reference: '',
      notes: '',
      generate_receipt: true
    });
    setIsHistoricalFormOpen(true);
  };

  const handleCloseHistoricalForm = () => {
    setIsHistoricalFormOpen(false);
  };

  const handleHistoricalSubmit = async (e) => {
    e.preventDefault();
    
    if (!historicalFormData.property_id || !historicalFormData.tenant_id || !historicalFormData.amount) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...historicalFormData,
        amount: parseFloat(historicalFormData.amount),
        payment_date: historicalFormData.payment_date.toISOString(),
        period_month: parseInt(historicalFormData.period_month),
        period_year: parseInt(historicalFormData.period_year)
      };

      await rentPaymentsAPI.createHistorical(submitData);
      toast.success(
        historicalFormData.generate_receipt 
          ? 'Ancien paiement enregistré avec quittance' 
          : 'Ancien paiement enregistré sans quittance'
      );
      
      handleCloseHistoricalForm();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.property_id || !formData.tenant_id || !formData.amount) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        payment_date: formData.payment_date.toISOString(),
        period_start: formData.period_start.toISOString(),
        period_end: formData.period_end.toISOString(),
      };

      await rentPaymentsAPI.create(submitData);
      toast.success('Paiement enregistré avec succès');
      
      handleCloseForm();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPayment) return;
    
    try {
      await rentPaymentsAPI.delete(selectedPayment.id);
      toast.success('Paiement supprimé avec succès');
      setIsDeleteOpen(false);
      setSelectedPayment(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleViewReceipt = (payment) => {
    setSelectedPayment(payment);
    setIsReceiptOpen(true);
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handleConfirmPayment = async (payment) => {
    setConfirmingPayment(payment.id);
    try {
      await rentPaymentsAPI.confirm(payment.id);
      toast.success('Paiement confirmé avec succès');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la confirmation';
      toast.error(message);
    } finally {
      setConfirmingPayment(null);
    }
  };

  const handleOpenCancel = (payment) => {
    setSelectedPayment(payment);
    setCancelReason('');
    setIsCancelOpen(true);
  };

  const handleCancelPayment = async () => {
    if (!selectedPayment) return;
    try {
      await rentPaymentsAPI.cancel(selectedPayment.id, cancelReason || null);
      toast.success('Quittance annulée. Tous les modules ont été recalculés.');
      setIsCancelOpen(false);
      setSelectedPayment(null);
      setCancelReason('');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'annulation';
      toast.error(message);
    }
  };

  const handleReinstate = async (payment) => {
    if (!window.confirm(`Réactiver la quittance ${payment.receipt_number || ''} ?\nLe montant sera de nouveau pris en compte dans les encaissements.`)) return;
    try {
      await rentPaymentsAPI.reinstate(payment.id);
      toast.success('Quittance réactivée');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la réactivation';
      toast.error(message);
    }
  };


  // Edit payment handlers
  const handleOpenEdit = (payment) => {
    setEditingPayment(payment);
    setEditFormData({
      payment_date: new Date(payment.payment_date),
      period_start: new Date(payment.period_start),
      period_end: new Date(payment.period_end),
      payment_method: payment.payment_method || 'especes',
      reference: payment.reference || '',
      notes: payment.notes || ''
    });
    setIsEditOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditOpen(false);
    setEditingPayment(null);
    setEditFormData({
      payment_date: new Date(),
      period_start: new Date(),
      period_end: new Date(),
      payment_method: 'especes',
      reference: '',
      notes: ''
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingPayment) return;

    setSaving(true);
    try {
      const submitData = {
        payment_date: editFormData.payment_date.toISOString(),
        period_start: editFormData.period_start.toISOString(),
        period_end: editFormData.period_end.toISOString(),
        payment_method: editFormData.payment_method,
        reference: editFormData.reference,
        notes: editFormData.notes
      };

      await rentPaymentsAPI.update(editingPayment.id, submitData);
      toast.success('Paiement modifié avec succès');
      handleCloseEdit();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la modification';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateDueNotices = async () => {
    setGeneratingNotices(true);
    try {
      const response = await dueNoticesAPI.generateForMonth();
      const data = response.data;
      
      if (data.created_count > 0) {
        toast.success(`${data.created_count} avis d'échéance créés pour ${data.month}`);
      } else if (data.skipped_count > 0) {
        toast.info(`Aucun nouvel avis créé. ${data.skipped_count} avis existent déjà.`);
      } else {
        toast.info('Aucun bail actif trouvé pour générer des avis.');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la génération';
      toast.error(message);
    } finally {
      setGeneratingNotices(false);
    }
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch =
      (payment.property_title && payment.property_title.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (payment.tenant_name && payment.tenant_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (payment.receipt_number && payment.receipt_number.toLowerCase().includes(searchQuery.toLowerCase()));

    const status = payment.status || 'en_attente';
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'pending' && (status === 'en_attente' || status === 'pending')) ||
      (statusFilter === 'confirmed' && status === 'confirme') ||
      (statusFilter === 'cancelled' && status === 'annule');

    return matchesSearch && matchesStatus;
  });

  const changeStatusFilter = (next) => {
    setStatusFilter(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('filter');
    else params.set('filter', next);
    setSearchParams(params, { replace: true });
  };

  // Taux de paiement du mois courant (base sur les paiements de loyer reels)
  const paymentRateStats = useMemo(() => {
    const now = new Date();
    const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    const expected = transactions
      .filter(t => t.transaction_type === 'location' && !['resilie', 'termine', 'annule', 'expire'].includes(t.status))
      .reduce((sum, t) => sum + (t.monthly_rent || t.amount || 0), 0);

    const collected = payments
      .filter(p => p.status === 'confirme' && (p.period_start || '').substring(0, 7) === monthKey)
      .reduce((sum, p) => sum + (p.amount || 0), 0);

    const rate = expected > 0 ? Math.round((collected / expected) * 1000) / 10 : 0;
    return { expected, collected, rate, monthLabel: now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) };
  }, [payments, transactions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="rent-payments-page">
      <ModuleTasksPanel moduleKey="rent_payments" moduleLabel="Paiements Loyer" />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex items-center gap-2">
            Paiements de Loyer
            <SyncBadge urlPattern="/rent-payments" />
          </h1>
          <p className="text-gray-500 mt-1">
            Gérez les quittances et reçus de loyer
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleGenerateDueNotices}
            variant="outline"
            className="border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373]/10"
            disabled={generatingNotices}
            data-testid="generate-due-notices-btn"
          >
            {generatingNotices ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <AlertCircle className="w-4 h-4 mr-2" />
            )}
            Générer les échéances
          </Button>
          <Button 
            onClick={handleOpenHistoricalForm}
            variant="outline"
            className="border-[#1B3A4B] text-[#1B3A4B] hover:bg-[#1B3A4B]/10"
            data-testid="add-historical-payment-btn"
            disabled={properties.length === 0 || tenants.length === 0}
          >
            <History className="w-4 h-4 mr-2" />
            Ancien paiement
          </Button>
          <Button 
            onClick={handleOpenForm}
            className="bg-[#1B3A4B] hover:bg-[#142D3A]"
            data-testid="add-rent-payment-btn"
            disabled={properties.length === 0 || tenants.length === 0}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau paiement
          </Button>
        </div>
      </div>

      {/* Warning if no rental properties or tenants */}
      {(properties.length === 0 || tenants.length === 0) && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4">
            <p className="text-yellow-800 text-sm">
              {properties.length === 0 && tenants.length === 0 
                ? 'Vous devez d\'abord ajouter des biens en location et des locataires.'
                : properties.length === 0 
                  ? 'Vous devez d\'abord ajouter des biens en location.'
                  : 'Vous devez d\'abord ajouter des locataires.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* KPI : Taux de paiement du mois (base sur les paiements de loyer reels) */}
      <Card className="kpi-premium rise-in" data-testid="payment-rate-card">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl shrink-0 shadow-inner" style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #FEF3C7 100%)' }}>
              <TrendingUp className="w-5 h-5 text-emerald-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-2">
                <p className="text-sm font-semibold text-[#1B3A4B]">
                  Taux de paiement — <span className="capitalize">{paymentRateStats.monthLabel}</span>
                </p>
                <span className="text-[11px] text-gray-400">(basé sur les paiements de loyer confirmés)</span>
              </div>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <p className="text-4xl font-extrabold tabular-nums text-gradient-gold" style={{ lineHeight: 1 }} data-testid="payment-rate-percentage">
                  {paymentRateStats.rate}%
                </p>
                <p className="text-sm text-gray-500 tabular-nums">
                  {formatCurrency(paymentRateStats.collected, 'XOF')} <span className="text-gray-300">/</span> {formatCurrency(paymentRateStats.expected, 'XOF')}
                </p>
              </div>
              <div className="mt-3 h-2.5 bg-slate-100 rounded-full overflow-hidden relative">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${Math.min(paymentRateStats.rate, 100)}%`,
                    background: 'linear-gradient(90deg, #10B981 0%, #D4A745 60%, #F2A900 100%)',
                  }}
                />
                <div className="absolute inset-0 progress-shimmer rounded-full" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search + Status filter */}
      <Card>
        <CardContent className="p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Rechercher par bien, locataire ou n° quittance..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-rent-payments-input"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {[
              { key: 'all', label: 'Tous' },
              { key: 'pending', label: 'En attente' },
              { key: 'confirmed', label: 'Confirmés' },
              { key: 'cancelled', label: 'Annulés' },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => changeStatusFilter(f.key)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition ${
                  statusFilter === f.key
                    ? 'bg-[#B85C38] text-white shadow'
                    : 'bg-[#F5EFE1] text-gray-700 hover:bg-[#F5EFE1]/70'
                }`}
                data-testid={`rp-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      {filteredPayments.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>N° Quittance</TableHead>
                  <TableHead>Bien</TableHead>
                  <TableHead>Locataire</TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayments.map((payment) => {
                  const status = payment.status || 'en_attente';
                  const statusInfo = statusLabels[status] || statusLabels.en_attente;
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                  <TableRow key={payment.id} className="row-premium">
                    <TableCell>
                      <span className="font-mono text-sm text-[#1B3A4B]">
                        {payment.receipt_number}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="truncate max-w-[150px]">
                          {payment.property_title || 'Bien supprimé'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span>{payment.tenant_name || 'Locataire supprimé'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {formatDate(payment.period_start)} - {formatDate(payment.period_end)}
                    </TableCell>
                    <TableCell className="font-semibold text-[#1B3A4B]">
                      {formatCurrency(payment.amount, payment.currency)}
                    </TableCell>
                    <TableCell>
                      <span className={statusInfo.badgeClass}>
                        <StatusIcon className="w-3 h-3" />
                        {statusInfo.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {formatDate(payment.payment_date)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {/* Confirm button - only for pending payments */}
                        {status === 'en_attente' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleConfirmPayment(payment)}
                            disabled={confirmingPayment === payment.id}
                            data-testid={`confirm-payment-${payment.id}`}
                            title="Confirmer le paiement"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            {confirmingPayment === payment.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewReceipt(payment)}
                          data-testid={`view-receipt-${payment.id}`}
                          title="Voir la quittance"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEdit(payment)}
                          data-testid={`edit-payment-${payment.id}`}
                          title="Modifier le paiement"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {payment.status === 'annule' ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleReinstate(payment)}
                            data-testid={`reinstate-payment-${payment.id}`}
                            title="Réactiver la quittance"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenCancel(payment)}
                            data-testid={`cancel-payment-${payment.id}`}
                            title="Annuler la quittance"
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setIsDeleteOpen(true);
                          }}
                          data-testid={`delete-payment-${payment.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Receipt className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun paiement trouvé</h3>
              <p className="text-gray-500 mb-4">
                {searchQuery
                  ? 'Essayez de modifier votre recherche'
                  : 'Commencez par enregistrer un paiement de loyer'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Form Modal */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enregistrer un paiement de loyer</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="property_id">Bien en location *</Label>
                <Select
                  value={formData.property_id}
                  onValueChange={(value) => {
                    const property = properties.find(p => p.id === value);
                    // Recupere le loyer du bail le plus recent pour ce bien (s'il n'y a qu'un seul locataire actif)
                    const leases = transactions.filter(
                      t => t.property_id === value && t.transaction_type === 'location'
                    );
                    const monthlyRent = leases.length === 1
                      ? (leases[0].monthly_rent || leases[0].amount)
                      : null;
                    setFormData({ 
                      ...formData, 
                      property_id: value,
                      tenant_id: '', // Reset tenant when property changes
                      amount: (monthlyRent || property?.price || '').toString(),
                      currency: leases[0]?.currency || property?.currency || formData.currency
                    });
                  }}
                >
                  <SelectTrigger data-testid="rent-property-select">
                    <SelectValue placeholder="Sélectionner un bien" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((property) => (
                      <SelectItem key={property.id} value={property.id}>
                        {property.title} - {formatCurrency(property.price, property.currency)}/mois
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="tenant_id">Locataire * {formData.property_id && `(${filteredTenantsForForm.length} locataire${filteredTenantsForForm.length > 1 ? 's' : ''} sur ce bien)`}</Label>
                <Select
                  value={formData.tenant_id}
                  onValueChange={(value) => {
                    // Recupere le loyer mensuel exact du bail entre ce locataire et ce bien
                    const lease = transactions.find(
                      t => t.property_id === formData.property_id
                        && t.client_id === value
                        && t.transaction_type === 'location'
                    );
                    const monthlyRent = lease?.monthly_rent || lease?.amount;
                    setFormData({
                      ...formData,
                      tenant_id: value,
                      amount: monthlyRent ? monthlyRent.toString() : formData.amount,
                      currency: lease?.currency || formData.currency,
                    });
                  }}
                  disabled={!formData.property_id}
                >
                  <SelectTrigger data-testid="rent-tenant-select">
                    <SelectValue placeholder={formData.property_id ? "Sélectionner un locataire" : "Sélectionnez d'abord un bien"} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredTenantsForForm.length === 0 ? (
                      <SelectItem value="none" disabled>Aucun locataire sur ce bien</SelectItem>
                    ) : (
                      filteredTenantsForForm.map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.name} - {tenant.phone}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-baseline justify-between gap-2">
                    <Label htmlFor="amount">Montant *</Label>
                    {newPaymentLeaseRent && (
                      <span
                        className="text-[11px] text-emerald-700 tabular-nums whitespace-nowrap"
                        data-testid="lease-rent-hint"
                        title="Loyer mensuel défini sur le bail"
                      >
                        Loyer du bail&nbsp;: {formatCurrency(newPaymentLeaseRent.amount, newPaymentLeaseRent.currency)}/mois
                      </span>
                    )}
                  </div>
                  {expectedRent?.is_prorated && (
                    <div
                      className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-1"
                      data-testid="prorata-hint"
                    >
                      Prorata 1er mois : {expectedRent.prorata_days}/{expectedRent.days_in_month} jours
                      {expectedRent.lease_start_date && (
                        <> (bail démarré le {new Date(expectedRent.lease_start_date).toLocaleDateString('fr-FR')})</>
                      )}
                      {' '}→ <b>{formatCurrency(expectedRent.expected_amount, formData.currency)}</b>
                    </div>
                  )}
                  <Input
                    id="amount"
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="150000"
                    data-testid="rent-amount-input"
                  />
                </div>
                <div>
                  <Label htmlFor="currency">Devise</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => setFormData({ ...formData, currency: value })}
                  >
                    <SelectTrigger data-testid="rent-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="XOF">FCFA (XOF)</SelectItem>
                      <SelectItem value="EUR">Euro (EUR)</SelectItem>
                      <SelectItem value="USD">Dollar (USD)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="rent-payment-date">Date de paiement *</Label>
                <Input
                  id="rent-payment-date"
                  type="date"
                  value={formData.payment_date ? format(formData.payment_date, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const newDate = new Date(e.target.value + 'T12:00:00');
                    const dates = calculatePeriodDates(formData.term_type, newDate);
                    setFormData({ ...formData, payment_date: newDate, ...dates });
                  }}
                  className="w-full"
                  data-testid="rent-payment-date-input"
                />
              </div>
              
              {/* Term Type Selection */}
              <div className="space-y-3">
                <Label>Type de terme *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant={formData.term_type === 'terme_echu' ? 'default' : 'outline'}
                    className={formData.term_type === 'terme_echu' 
                      ? 'bg-[#1B3A4B] hover:bg-[#142D3A] h-auto py-3' 
                      : 'h-auto py-3'}
                    onClick={() => {
                      const dates = calculatePeriodDates('terme_echu', formData.payment_date);
                      setFormData({ 
                        ...formData, 
                        term_type: 'terme_echu',
                        ...dates
                      });
                    }}
                    data-testid="term-echu-btn"
                  >
                    <div className="text-center">
                      <div className="font-semibold">Terme échu</div>
                      <div className="text-xs opacity-80">Mois précédent</div>
                    </div>
                  </Button>
                  <Button
                    type="button"
                    variant={formData.term_type === 'terme_a_echoir' ? 'default' : 'outline'}
                    className={formData.term_type === 'terme_a_echoir' 
                      ? 'bg-[#D4A373] hover:bg-[#c49363] h-auto py-3' 
                      : 'h-auto py-3'}
                    onClick={() => {
                      const dates = calculatePeriodDates('terme_a_echoir', formData.payment_date);
                      setFormData({ 
                        ...formData, 
                        term_type: 'terme_a_echoir',
                        ...dates
                      });
                    }}
                    data-testid="term-echoir-btn"
                  >
                    <div className="text-center">
                      <div className="font-semibold">Terme à échoir</div>
                      <div className="text-xs opacity-80">Mois en cours</div>
                    </div>
                  </Button>
                </div>
              </div>
              
              {/* Period Display (read-only, calculated automatically) */}
              <div className="bg-gray-50 rounded-lg p-4">
                <Label className="text-gray-600 mb-2 block">Période couverte (calculée automatiquement)</Label>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-white border rounded-md px-3 py-2 text-center">
                    <span className="text-sm text-gray-500">Du</span>
                    <p className="font-medium">{format(formData.period_start, "dd MMMM yyyy", { locale: fr })}</p>
                  </div>
                  <span className="text-gray-400">→</span>
                  <div className="flex-1 bg-white border rounded-md px-3 py-2 text-center">
                    <span className="text-sm text-gray-500">Au</span>
                    <p className="font-medium">{format(formData.period_end, "dd MMMM yyyy", { locale: fr })}</p>
                  </div>
                </div>
              </div>
              
              <div>
                <Label htmlFor="payment_method">Mode de paiement</Label>
                <Select
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                >
                  <SelectTrigger data-testid="rent-payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="especes">Espèces</SelectItem>
                    <SelectItem value="virement">Virement bancaire</SelectItem>
                    <SelectItem value="mobile_money">Mobile Money</SelectItem>
                    <SelectItem value="cheque">Chèque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="reference">Référence (optionnel)</Label>
                <Input
                  id="reference"
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  placeholder="Numéro de transaction..."
                  data-testid="rent-reference-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                disabled={saving}
                data-testid="rent-submit-btn"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt Preview Modal */}
      <Dialog open={isReceiptOpen} onOpenChange={setIsReceiptOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Quittance de Loyer</DialogTitle>
          </DialogHeader>
          
          {selectedPayment && (
            <div className="print:p-8" id="receipt-content">
              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                {/* Header */}
                <div className="flex justify-between items-start mb-6 pb-4 border-b">
                  <div>
                    <h2 className="text-2xl font-bold text-[#1B3A4B]">ImmoMali</h2>
                    <p className="text-sm text-gray-500">Gestion Immobilière</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold">QUITTANCE DE LOYER</p>
                    <p className="text-sm text-gray-500">N° {selectedPayment.receipt_number}</p>
                  </div>
                </div>
                
                {/* Details */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Locataire</h3>
                    <p className="font-semibold">{selectedPayment.tenant_name}</p>
                    <p className="text-sm text-gray-600">{selectedPayment.tenant_phone}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Bien loué</h3>
                    <p className="font-semibold">{selectedPayment.property_title}</p>
                    <p className="text-sm text-gray-600">{selectedPayment.property_address}</p>
                  </div>
                </div>
                
                {/* Payment Info */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Période</p>
                      <p className="font-medium">
                        Du {formatDate(selectedPayment.period_start)} au {formatDate(selectedPayment.period_end)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date de paiement</p>
                      <p className="font-medium">{formatDate(selectedPayment.payment_date)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Mode de paiement</p>
                      <p className="font-medium">{paymentMethodLabels[selectedPayment.payment_method]}</p>
                    </div>
                    {selectedPayment.reference && (
                      <div>
                        <p className="text-sm text-gray-500">Référence</p>
                        <p className="font-medium">{selectedPayment.reference}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Amount */}
                <div className="text-center py-4 border-t border-b mb-6">
                  <p className="text-sm text-gray-500 mb-1">Montant payé</p>
                  <p className="text-3xl font-bold text-[#1B3A4B]">
                    {formatCurrency(selectedPayment.amount, selectedPayment.currency)}
                  </p>
                </div>
                
                {/* Footer */}
                <div className="text-center text-sm text-gray-500">
                  <p>Cette quittance atteste du paiement du loyer pour la période indiquée.</p>
                  <p className="mt-2">Émise le {formatDate(selectedPayment.created_at)}</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReceiptOpen(false)}>
              Fermer
            </Button>
            <Button 
              onClick={handlePrintReceipt}
              className="bg-[#1B3A4B] hover:bg-[#142D3A]"
              data-testid="print-receipt-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Imprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce paiement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La quittance sera définitivement supprimée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-payment-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Quittance Dialog */}
      <Dialog open={isCancelOpen} onOpenChange={setIsCancelOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-orange-600" />
              Annuler la quittance
            </DialogTitle>
            <DialogDescription>
              {selectedPayment && (
                <>
                  Quittance <span className="font-mono font-semibold">{selectedPayment.receipt_number}</span> · {selectedPayment.tenant_name} ·{' '}
                  <span className="font-semibold">{formatCurrency(selectedPayment.amount, selectedPayment.currency)}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm text-gray-700 bg-amber-50 border border-amber-200 rounded-md p-3">
              Cette action <b>n&apos;effacera pas</b> la quittance (elle reste dans l&apos;historique en statut &quot;Annulé&quot;).
              <br />
              Le montant sera <b>retiré automatiquement</b> du tableau de bord, du bilan propriétaire, des relevés financiers et du taux d&apos;encaissement.
              <br />
              L&apos;avis d&apos;échéance associé repassera en &quot;En attente&quot;.
            </div>
            <div>
              <Label htmlFor="cancel-reason">Motif d&apos;annulation (optionnel)</Label>
              <Textarea
                id="cancel-reason"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Ex: chèque rejeté, erreur de saisie, paiement remboursé..."
                data-testid="cancel-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCancelOpen(false)}>Fermer</Button>
            <Button
              onClick={handleCancelPayment}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-cancel-payment-btn"
            >
              <Ban className="w-4 h-4 mr-2" />
              Annuler la quittance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Historical Payment Form Modal */}
      <Dialog open={isHistoricalFormOpen} onOpenChange={setIsHistoricalFormOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#D4A373]" />
              Enregistrer un ancien paiement
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleHistoricalSubmit} className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="hist_property_id">Bien en location *</Label>
                <Select
                  value={historicalFormData.property_id}
                  onValueChange={(value) => {
                    const property = properties.find(p => p.id === value);
                    const leases = transactions.filter(
                      t => t.property_id === value && t.transaction_type === 'location'
                    );
                    const monthlyRent = leases.length === 1
                      ? (leases[0].monthly_rent || leases[0].amount)
                      : null;
                    setHistoricalFormData({ 
                      ...historicalFormData, 
                      property_id: value,
                      tenant_id: '', // Reset tenant when property changes
                      amount: (monthlyRent || property?.price || '').toString(),
                      currency: leases[0]?.currency || property?.currency || historicalFormData.currency
                    });
                  }}
                >
                  <SelectTrigger data-testid="hist-property-select">
                    <SelectValue placeholder="Sélectionner un bien" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((property) => (
                      <SelectItem key={property.id} value={property.id}>
                        {property.title} - {formatCurrency(property.price, property.currency)}/mois
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="hist_tenant_id">Locataire * {historicalFormData.property_id && `(${filteredTenantsForHistorical.length} locataire${filteredTenantsForHistorical.length > 1 ? 's' : ''} sur ce bien)`}</Label>
                <Select
                  value={historicalFormData.tenant_id}
                  onValueChange={(value) => {
                    const lease = transactions.find(
                      t => t.property_id === historicalFormData.property_id
                        && t.client_id === value
                        && t.transaction_type === 'location'
                    );
                    const monthlyRent = lease?.monthly_rent || lease?.amount;
                    setHistoricalFormData({
                      ...historicalFormData,
                      tenant_id: value,
                      amount: monthlyRent ? monthlyRent.toString() : historicalFormData.amount,
                      currency: lease?.currency || historicalFormData.currency,
                    });
                  }}
                  disabled={!historicalFormData.property_id}
                >
                  <SelectTrigger data-testid="hist-tenant-select">
                    <SelectValue placeholder={historicalFormData.property_id ? "Sélectionner un locataire" : "Sélectionnez d'abord un bien"} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredTenantsForHistorical.length === 0 ? (
                      <SelectItem value="none" disabled>Aucun locataire sur ce bien</SelectItem>
                    ) : (
                      filteredTenantsForHistorical.map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.name} - {tenant.phone}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="hist_period_month">Mois *</Label>
                  <Select
                    value={historicalFormData.period_month.toString()}
                    onValueChange={(value) => setHistoricalFormData({ ...historicalFormData, period_month: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="hist-month-select">
                      <SelectValue placeholder="Mois" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Janvier</SelectItem>
                      <SelectItem value="2">Février</SelectItem>
                      <SelectItem value="3">Mars</SelectItem>
                      <SelectItem value="4">Avril</SelectItem>
                      <SelectItem value="5">Mai</SelectItem>
                      <SelectItem value="6">Juin</SelectItem>
                      <SelectItem value="7">Juillet</SelectItem>
                      <SelectItem value="8">Août</SelectItem>
                      <SelectItem value="9">Septembre</SelectItem>
                      <SelectItem value="10">Octobre</SelectItem>
                      <SelectItem value="11">Novembre</SelectItem>
                      <SelectItem value="12">Décembre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="hist_period_year">Année *</Label>
                  <Select
                    value={historicalFormData.period_year.toString()}
                    onValueChange={(value) => setHistoricalFormData({ ...historicalFormData, period_year: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="hist-year-select">
                      <SelectValue placeholder="Année" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-baseline justify-between gap-2">
                    <Label htmlFor="hist_amount">Montant payé *</Label>
                    {historicalLeaseRent && (
                      <span
                        className="text-[11px] text-emerald-700 tabular-nums whitespace-nowrap"
                        data-testid="hist-lease-rent-hint"
                        title="Loyer mensuel défini sur le bail"
                      >
                        Loyer du bail&nbsp;: {formatCurrency(historicalLeaseRent.amount, historicalLeaseRent.currency)}/mois
                      </span>
                    )}
                  </div>
                  {historicalExpectedRent?.is_prorated && (
                    <div
                      className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-1"
                      data-testid="hist-prorata-hint"
                    >
                      Prorata 1er mois : {historicalExpectedRent.prorata_days}/{historicalExpectedRent.days_in_month} jours
                      {historicalExpectedRent.lease_start_date && (
                        <> (bail démarré le {new Date(historicalExpectedRent.lease_start_date).toLocaleDateString('fr-FR')})</>
                      )}
                      {' '}→ <b>{formatCurrency(historicalExpectedRent.expected_amount, historicalFormData.currency)}</b>
                    </div>
                  )}
                  <Input
                    id="hist_amount"
                    type="number"
                    value={historicalFormData.amount}
                    onChange={(e) => setHistoricalFormData({ ...historicalFormData, amount: e.target.value })}
                    placeholder="0"
                    data-testid="hist-amount-input"
                  />
                </div>
                <div>
                  <Label htmlFor="hist_currency">Devise</Label>
                  <Select
                    value={historicalFormData.currency}
                    onValueChange={(value) => setHistoricalFormData({ ...historicalFormData, currency: value })}
                  >
                    <SelectTrigger data-testid="hist-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="XOF">FCFA (XOF)</SelectItem>
                      <SelectItem value="EUR">Euro (EUR)</SelectItem>
                      <SelectItem value="USD">Dollar (USD)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="hist_payment_date">Date du paiement</Label>
                <Input
                  id="hist_payment_date"
                  type="date"
                  value={historicalFormData.payment_date ? format(historicalFormData.payment_date, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    setHistoricalFormData({ ...historicalFormData, payment_date: new Date(e.target.value + 'T12:00:00') });
                  }}
                  className="w-full"
                  data-testid="hist-payment-date-input"
                />
              </div>

              <div>
                <Label htmlFor="hist_payment_method">Mode de paiement</Label>
                <Select
                  value={historicalFormData.payment_method}
                  onValueChange={(value) => setHistoricalFormData({ ...historicalFormData, payment_method: value })}
                >
                  <SelectTrigger data-testid="hist-payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="especes">Espèces</SelectItem>
                    <SelectItem value="virement">Virement bancaire</SelectItem>
                    <SelectItem value="mobile_money">Mobile Money</SelectItem>
                    <SelectItem value="cheque">Chèque</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="hist_reference">Référence (optionnel)</Label>
                <Input
                  id="hist_reference"
                  value={historicalFormData.reference}
                  onChange={(e) => setHistoricalFormData({ ...historicalFormData, reference: e.target.value })}
                  placeholder="N° chèque, référence virement..."
                  data-testid="hist-reference-input"
                />
              </div>

              <div>
                <Label htmlFor="hist_notes">Notes (optionnel)</Label>
                <Input
                  id="hist_notes"
                  value={historicalFormData.notes}
                  onChange={(e) => setHistoricalFormData({ ...historicalFormData, notes: e.target.value })}
                  placeholder="Notes ou commentaires..."
                  data-testid="hist-notes-input"
                />
              </div>

              <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="hist_generate_receipt"
                  checked={historicalFormData.generate_receipt}
                  onChange={(e) => setHistoricalFormData({ ...historicalFormData, generate_receipt: e.target.checked })}
                  className="h-4 w-4 text-[#1B3A4B] border-gray-300 rounded focus:ring-[#1B3A4B]"
                  data-testid="hist-generate-receipt-checkbox"
                />
                <Label htmlFor="hist_generate_receipt" className="text-sm font-medium cursor-pointer">
                  Générer une quittance pour ce paiement
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseHistoricalForm}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                disabled={saving}
                data-testid="submit-historical-payment-btn"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <History className="w-4 h-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-600" />
              Modifier le paiement
            </DialogTitle>
          </DialogHeader>
          
          {editingPayment && (
            <form onSubmit={handleEditSubmit} className="space-y-4">
              {/* Read-only info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">N° Quittance</span>
                  <span className="font-mono text-sm font-medium">{editingPayment.receipt_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Bien</span>
                  <span className="text-sm font-medium">{editingPayment.property_title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Locataire</span>
                  <span className="text-sm font-medium">{editingPayment.tenant_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Montant</span>
                  <span className="text-sm font-semibold text-[#1B3A4B]">
                    {formatCurrency(editingPayment.amount, editingPayment.currency)}
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                {/* Date de paiement */}
                <div>
                  <Label htmlFor="edit_payment_date">Date de paiement</Label>
                  <Input
                    id="edit_payment_date"
                    type="date"
                    value={editFormData.payment_date ? format(editFormData.payment_date, 'yyyy-MM-dd') : ''}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      setEditFormData({ ...editFormData, payment_date: new Date(e.target.value + 'T12:00:00') });
                    }}
                    className="w-full"
                    data-testid="edit-payment-date-input"
                  />
                </div>

                {/* Période - Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit_period_start">Début de période</Label>
                    <Input
                      id="edit_period_start"
                      type="date"
                      value={editFormData.period_start ? format(editFormData.period_start, 'yyyy-MM-dd') : ''}
                      onChange={(e) => {
                        if (!e.target.value) return;
                        setEditFormData({ ...editFormData, period_start: new Date(e.target.value + 'T12:00:00') });
                      }}
                      className="w-full"
                      data-testid="edit-period-start-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_period_end">Fin de période</Label>
                    <Input
                      id="edit_period_end"
                      type="date"
                      value={editFormData.period_end ? format(editFormData.period_end, 'yyyy-MM-dd') : ''}
                      onChange={(e) => {
                        if (!e.target.value) return;
                        setEditFormData({ ...editFormData, period_end: new Date(e.target.value + 'T12:00:00') });
                      }}
                      className="w-full"
                      data-testid="edit-period-end-input"
                    />
                  </div>
                </div>

                {/* Mode de paiement */}
                <div>
                  <Label htmlFor="edit_payment_method">Mode de paiement</Label>
                  <Select
                    value={editFormData.payment_method}
                    onValueChange={(value) => setEditFormData({ ...editFormData, payment_method: value })}
                  >
                    <SelectTrigger data-testid="edit-payment-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="especes">Espèces</SelectItem>
                      <SelectItem value="virement">Virement bancaire</SelectItem>
                      <SelectItem value="mobile_money">Mobile Money</SelectItem>
                      <SelectItem value="cheque">Chèque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Référence */}
                <div>
                  <Label htmlFor="edit_reference">Référence (optionnel)</Label>
                  <Input
                    id="edit_reference"
                    value={editFormData.reference}
                    onChange={(e) => setEditFormData({ ...editFormData, reference: e.target.value })}
                    placeholder="N° chèque, référence virement..."
                    data-testid="edit-reference-input"
                  />
                </div>

                {/* Notes */}
                <div>
                  <Label htmlFor="edit_notes">Notes (optionnel)</Label>
                  <Input
                    id="edit_notes"
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                    placeholder="Notes ou commentaires..."
                    data-testid="edit-notes-input"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseEdit}>
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={saving}
                  data-testid="submit-edit-payment-btn"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Pencil className="w-4 h-4 mr-2" />
                      Enregistrer les modifications
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RentPayments;
